/**
 * Les DEUX axes du parcours d'un colis Befret — à ne jamais confondre.
 *
 * Un envoi traverse deux phases portées par deux acteurs différents, et
 * chacune a son propre choix client :
 *
 * 1. **Collecte DPD en Europe** (phase 1) — *comment le colis quitte le client* :
 *    DPD vient le chercher à domicile, ou le client le dépose en point relais DPD.
 *    → {@link DpdCollectionService}
 * 2. **Livraison finale Befret en RDC** (phase 2) — *comment le destinataire
 *    reçoit le colis* : retrait en agence Befret (Gombe / Lubumbashi), ou
 *    livraison à son domicile.
 *    → {@link BefretDeliveryMethod}
 *
 * Les deux unions sont **disjointes** : aucune valeur n'appartient aux deux.
 * C'est le compilateur qui garde la frontière — une valeur DPD assignée à un
 * champ RDC (ou l'inverse) ne compile pas (`delivery-axes.models.spec.ts`).
 *
 * Les VALEURS sont celles persistées en Firestore (`standardData.serviceType`,
 * `standardData.befretDeliveryMethod`, `parcelInfo.deliveryMethod`) et
 * échangées avec les Cloud Functions : elles ne se renomment pas ici.
 *
 * Vocabulaire à l'écran (glossaire #1548) :
 * - `home_pickup`   → « Collecte DPD à domicile » (Europe)
 * - `shop_pickup`   → « Dépôt en point relais DPD » (Europe)
 * - `warehouse`     → « Retrait en agence Befret » (RDC)
 * - `home_delivery` → « Livraison à domicile en RDC »
 *
 * Le mot « point relais » ne désigne QUE DPD ; l'adresse RDC est une « agence
 * Befret », jamais un point relais.
 */

/** Les services de collecte DPD (Europe, phase 1), dans l'ordre d'affichage. */
export const DPD_COLLECTION_SERVICES = ['home_pickup', 'shop_pickup'] as const;

/**
 * Service DPD retenu pour la collecte en Europe (phase 1).
 *
 * - `home_pickup` — DPD collecte le colis au domicile de l'expéditeur
 *   (« Collecte DPD à domicile »).
 * - `shop_pickup` — l'expéditeur dépose le colis dans un point relais DPD
 *   (« Dépôt en point relais DPD »).
 *
 * Champs qui portent cette notion : `serviceType`, `dpdService`,
 * `dpdServiceType`, `selectedPickupPoint` (le point relais DPD choisi).
 * Rien ici ne dit comment le colis sera remis en RDC.
 */
export type DpdCollectionService = typeof DPD_COLLECTION_SERVICES[number];

/** Les modes de remise Befret en RDC (phase 2), dans l'ordre d'affichage. */
export const BEFRET_DELIVERY_METHODS = ['warehouse', 'home_delivery'] as const;

/**
 * Mode de remise finale par Befret en RDC (phase 2).
 *
 * - `warehouse` — le destinataire retire le colis à l'agence Befret de sa
 *   ville (« Retrait en agence Befret », gratuit).
 * - `home_delivery` — Befret livre au domicile du destinataire
 *   (« Livraison à domicile en RDC », tarifée au poids).
 *
 * Champs qui portent cette notion : `pickupMethod` (contrôle du formulaire
 * d'envoi et brouillons — nom historique, à lire comme *befretDeliveryMethod*),
 * `befretDeliveryMethod`, `parcelInfo.deliveryMethod`.
 * Rien ici ne dit comment DPD a collecté le colis en Europe.
 */
export type BefretDeliveryMethod = typeof BEFRET_DELIVERY_METHODS[number];

/** Vrai si `value` est un service de collecte DPD (Europe). */
export function isDpdCollectionService(value: unknown): value is DpdCollectionService {
  return typeof value === 'string' && (DPD_COLLECTION_SERVICES as readonly string[]).includes(value);
}

/** Vrai si `value` est un mode de remise Befret en RDC. */
export function isBefretDeliveryMethod(value: unknown): value is BefretDeliveryMethod {
  return typeof value === 'string' && (BEFRET_DELIVERY_METHODS as readonly string[]).includes(value);
}

/**
 * #1571 — les formes LEGACY de la remise RDC, et ce qu'elles valent aujourd'hui.
 *
 * Le vocabulaire de la phase 2 a changé (#1548) : « point relais » ne désigne
 * plus que DPD, et l'adresse RDC est une « agence Befret ». Les valeurs
 * 'pickup_point', 'point_relais', 'pickup' et 'locker' ont donc disparu du
 * catalogue d'affichage — mais rien ne garantit qu'aucun document Firestore ne
 * les porte : ce sont des données PERSISTÉES, pas des constantes de code.
 *
 * Mesuré le 2026-08-29 sur les trois projets : zéro document avec
 * parcelInfo.deliveryMethod == 'pickup_point'. Cette table n'existe donc pas
 * pour réparer un incident, mais pour fermer la CLASSE — « une valeur tombée du
 * catalogue » — qui produisait jusqu'ici une clé de traduction brute à l'écran.
 */
const LEGACY_BEFRET_DELIVERY_METHODS: Readonly<Record<string, BefretDeliveryMethod>> = {
  // Remise à l'agence Befret — anciens noms du même geste.
  pickup_point: 'warehouse',
  point_relais: 'warehouse',
  pickup: 'warehouse',
  locker: 'warehouse',
  // Livraison au domicile du destinataire — anciens noms du même geste.
  domicile: 'home_delivery',
  home: 'home_delivery',
};

/**
 * Ramène une valeur persistée de remise RDC à l'une des DEUX valeurs
 * canoniques, ou à `null` si elle n'est reconnue ni comme canonique ni comme
 * legacy.
 *
 * `null` est délibéré. Replier silencieusement une valeur inconnue sur
 * 'home_delivery' masquerait la question au lieu de la trancher (doctrine
 * #1006) : l'appelant doit décider ce qu'il montre d'une valeur qu'il ne sait
 * pas nommer — et ce ne peut pas être la valeur elle-même, sinon on retombe
 * exactement sur la clé brute que ce normaliseur existe pour éviter.
 *
 * ⚠️ Ce normaliseur ne juge QUE l'axe Befret/RDC. Une valeur de l'axe DPD
 * ('home_pickup', 'shop_pickup') rend `null`, et c'est correct : elle ne dit
 * rien de la remise finale. Ne pas l'y ajouter — c'est précisément la confusion
 * que #1548 a séparée.
 */
export function normalizeBefretDeliveryMethod(value: unknown): BefretDeliveryMethod | null {
  if (isBefretDeliveryMethod(value)) return value;
  if (typeof value !== 'string') return null;
  return LEGACY_BEFRET_DELIVERY_METHODS[value.trim().toLowerCase()] ?? null;
}
